import Vue from 'vue';
import {
  doc,
  getDoc,
} from 'firebase/firestore';
import { getBlob, getDownloadURL, ref } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { isAuthorizedReckonUser, isPublished } from '../lib/draftMode';

const assetRequests = new Map();
const mediaState = Vue.observable({ version: 0 });

function normalizeSourcePath(sourcePath = '') {
  return sourcePath
    .replace(/\\/g, '/')
    .replace(/^src\/assets\//, '')
    .replace(/^\//, '');
}

export function assetIdForSourcePath(sourcePath) {
  const normalized = normalizeSourcePath(sourcePath);
  const slug = normalized
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `image-${slug}`;
}

export function mediaRef(sourcePath, metadata = {}) {
  const normalized = normalizeSourcePath(sourcePath);
  return {
    ...metadata,
    firebaseAssetId: metadata.firebaseAssetId || assetIdForSourcePath(normalized),
    sourcePath: normalized,
    src: metadata.src || '',
  };
}

function isMediaRef(asset) {
  return Boolean(asset && typeof asset === 'object' && asset.firebaseAssetId);
}

function mediaRefFromValue(asset) {
  if (isMediaRef(asset)) return asset;
  if (isMediaRef(asset?.src)) return asset.src;
  return null;
}

function validateStoragePath(storagePath, assetId, status) {
  const valid = isPublished(status)
    ? /^public\/(site|reckon-way\/events)\//.test(storagePath || '')
    : /^draft\//.test(storagePath || '');
  if (!valid) {
    throw new Error(`Media asset ${assetId} has an invalid storage path for its status.`);
  }
}

async function fetchMediaAsset(assetId) {
  const snapshot = await getDoc(doc(db, 'mediaAssets', assetId));
  if (!snapshot.exists()) throw new Error(`Media asset ${assetId} was not found.`);

  const metadata = snapshot.data();
  if (!isPublished(metadata.status) && !isAuthorizedReckonUser(auth.currentUser)) {
    throw new Error(`Media asset ${assetId} is not available.`);
  }
  validateStoragePath(metadata.storagePath, assetId, metadata.status);

  const storageRef = ref(storage, metadata.storagePath);
  const src = isPublished(metadata.status)
    ? await getDownloadURL(storageRef)
    : URL.createObjectURL(await getBlob(storageRef));
  return {
    ...metadata,
    id: snapshot.id,
    src,
  };
}

export function clearDraftMediaAssets() {
  assetRequests.forEach((request, assetId) => {
    request.then((asset) => {
      if (asset.status === 'draft' && asset.src?.startsWith('blob:')) URL.revokeObjectURL(asset.src);
    }).catch(() => {});
    request.then((asset) => {
      if (asset.status === 'draft') assetRequests.delete(assetId);
    }).catch(() => assetRequests.delete(assetId));
  });
  mediaState.version += 1;
}

function getAssetRequest(assetId) {
  if (!assetRequests.has(assetId)) assetRequests.set(assetId, fetchMediaAsset(assetId));
  return assetRequests.get(assetId);
}

export function resolveMediaAsset(asset) {
  const target = mediaRefFromValue(asset);
  if (!target) return Promise.resolve(asset);

  return getAssetRequest(target.firebaseAssetId).then((metadata) => {
    Object.assign(target, metadata, { firebaseAssetId: target.firebaseAssetId });
    mediaState.version += 1;

    if (target === asset) return { ...asset, ...metadata, firebaseAssetId: target.firebaseAssetId };
    return { ...asset, src: metadata.src };
  });
}

async function fetchMediaMetadataBatch(assetIds) {
  const uniqueIds = [...new Set(assetIds.filter(Boolean))];
  const metadataById = new Map();
  await Promise.all(uniqueIds.map(async (assetId) => {
    try {
      metadataById.set(assetId, await getAssetRequest(assetId));
    } catch (error) {
      console.warn(`Unable to load Firebase media asset ${assetId}.`, error);
    }
  }));

  return metadataById;
}

export async function resolveMediaAssets(assets = []) {
  const targets = assets
    .filter(Boolean)
    .map(mediaRefFromValue)
    .filter(Boolean);
  const metadataById = await fetchMediaMetadataBatch(targets.map((asset) => asset.firebaseAssetId));

  const resolved = await Promise.all(assets.filter(Boolean).map(async (asset) => {
    const target = mediaRefFromValue(asset);
    if (!target) return asset;

    const metadata = metadataById.get(target.firebaseAssetId);
    if (!metadata) {
      console.warn(`Unable to load Firebase media asset ${target.firebaseAssetId}.`);
      return null;
    }

    Object.assign(target, metadata, { firebaseAssetId: target.firebaseAssetId });
    if (target === asset) return { ...asset, ...metadata, firebaseAssetId: target.firebaseAssetId };
    return { ...asset, src: metadata.src };
  }));

  mediaState.version += 1;
  return resolved.filter(Boolean);
}

export function mediaSrc(asset) {
  // Reading the reactive version makes every image binding rerender when a lazy
  // Storage URL arrives, without preloading every asset at app startup.
  const mediaUpdatesEnabled = mediaState.version >= 0;

  if (!asset || !mediaUpdatesEnabled) return '';
  if (Array.isArray(asset)) {
    return asset.map((candidate) => mediaSrc(candidate)).find(Boolean) || '';
  }
  if (typeof asset === 'string') return asset;

  const target = mediaRefFromValue(asset);
  if (target) {
    if (!target.src && !target.mediaError) {
      getAssetRequest(target.firebaseAssetId)
        .then((metadata) => {
          Object.assign(target, metadata, { firebaseAssetId: target.firebaseAssetId });
          mediaState.version += 1;
        })
        .catch((error) => {
          target.mediaError = error.message;
          mediaState.version += 1;
          console.warn(`Unable to load Firebase media asset ${target.firebaseAssetId}.`, error);
        });
    }
    return target.src || '';
  }

  return asset.src ? mediaSrc(asset.src) : '';
}

export default resolveMediaAssets;
